/**
 * 歌モノ (トーン option の metadata.bgm_mode==='lyrics') 選択時の想定尺下限 30 秒
 * (docs/features/scenario-song-lyrics-bgm-plan.md §5.1.4)。
 *
 * Lyria Pro は 30 秒未満の曲を作れないため、歌モノトーンを選んだ状態で想定尺が
 * 30 秒未満の option を disable し、選択中の値が 30 秒未満なら 30 秒以上の最小
 * option へ自動で引き上げる。純粋関数として切り出し、ScenarioGenerator.tsx /
 * ScenarioSettingsSection.tsx から使う (テスト容易性のため)。
 */
import type { HierarchicalOption } from '../../common/HierarchicalSelect';

export const LYRICS_MIN_DURATION_SECONDS = 30;

export type DurationOption = { value: string; label: string; metadata?: Record<string, unknown> | null };

/** ネストした group を深さ優先で辿り、key と一致する option の metadata を返す。 */
export function findHierarchicalOptionMetadata(
  options: HierarchicalOption[],
  key: string,
): Record<string, unknown> | null | undefined {
  for (const opt of options) {
    if (opt.key === key) return opt.metadata;
    if (opt.children) {
      const found = findHierarchicalOptionMetadata(opt.children, key);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/** 選択中トーン key が歌モノ (metadata.bgm_mode==='lyrics') か。 */
export function isLyricsBgmTone(toneOptions: HierarchicalOption[], toneKey: string): boolean {
  if (!toneKey) return false;
  return findHierarchicalOptionMetadata(toneOptions, toneKey)?.bgm_mode === 'lyrics';
}

function optionSeconds(opt: DurationOption): number | null {
  return typeof opt.metadata?.seconds === 'number' ? (opt.metadata!.seconds as number) : null;
}

/** 想定尺 option が歌モノの下限 (30 秒) 未満か (metadata.seconds が無い option は対象外=false)。 */
export function isDurationBelowLyricsMin(opt: DurationOption | undefined): boolean {
  if (!opt) return false;
  const seconds = optionSeconds(opt);
  return seconds != null && seconds < LYRICS_MIN_DURATION_SECONDS;
}

/** 30 秒以上を満たす最小の想定尺 option (無ければ null)。 */
export function findMinLyricsDurationOption(durationOptions: DurationOption[]): DurationOption | null {
  const candidates = durationOptions
    .filter((o) => o.value && (optionSeconds(o) ?? -1) >= LYRICS_MIN_DURATION_SECONDS)
    .sort((a, b) => (optionSeconds(a) as number) - (optionSeconds(b) as number));
  return candidates[0] ?? null;
}
